export const GUEST_KEY = 'goodvale_presentation_guest';
export const accountKey = id => `goodvale_presentation_account:${id}`;
const read = (storage, key) => { try { return JSON.parse(storage.getItem(key)); } catch { return null; } };
const write = (storage, key, value) => { try { storage.setItem(key, JSON.stringify(value)); } catch { /* Session remains usable without storage. */ } };

/** Account-scoped optimistic preference. Serialized saves prevent an old response winning a rapid switch. */
export function createPreferences({ catalog, storage, save, onMode = () => {} }) {
  let session = null;
  let generation = 0;
  let revision = 0;
  let queue = Promise.resolve();
  let explicitGuestChoice = null;
  let lastLoad;
  const listeners = new Set();
  let state = { preference: null, mode: catalog.fallback, loading: false, needsChoice: true, pending: false, error: false, unsupported: false, loadError: false };
  const emit = patch => {
    state = { ...state, ...patch };
    onMode(state.mode);
    listeners.forEach(listener => listener());
  };
  const display = preference => ({ preference, mode: catalog.resolve(preference), needsChoice: !preference, unsupported: Boolean(preference && !catalog.accepts(preference)) });
  const cache = value => write(storage, session ? accountKey(session.id) : GUEST_KEY, value);
  function persist(id) {
    const owner = session;
    const turn = generation;
    const change = revision;
    emit({ pending: true, error: false });
    const task = queue.catch(() => {}).then(async () => {
      // Do not start a queued write after the owner has logged out or changed accounts.
      if (turn !== generation) return;
      await save(id, owner);
      if (turn !== generation || change !== revision) return;
      cache({ preference: id, pending: false });
      emit({ pending: false, error: false });
    }).catch(() => {
      if (turn === generation && change === revision) emit({ pending: false, error: true });
    });
    queue = task;
    return task;
  }
  return {
    subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener); },
    getSnapshot: () => state,
    async connect(nextSession, load) {
      session = nextSession;
      lastLoad = load;
      const turn = ++generation;
      const atRevision = ++revision;
      const cached = read(storage, session ? accountKey(session.id) : GUEST_KEY);
      emit({ ...display(cached?.preference || null), loading: Boolean(session && !cached), error: false, pending: false, loadError: false });
      if (!session) return;
      try {
        const profile = await load(nextSession);
        if (turn !== generation || atRevision !== revision) return;
        if (cached?.pending && catalog.accepts(cached.preference)) {
          emit({ loading: false });
          return persist(cached.preference);
        }
        if (!profile.presentationMode && explicitGuestChoice) {
          const choice = explicitGuestChoice;
          explicitGuestChoice = null;
          this.choose(choice);
          return;
        }
        explicitGuestChoice = null;
        const preference = profile.presentationMode || null;
        cache({ preference, pending: false });
        emit({ ...display(preference), loading: false });
      } catch {
        if (turn === generation && atRevision === revision) emit({ loading: false, error: true, loadError: !cached?.preference });
      }
    },
    choose(id) {
      if (!catalog.accepts(id)) throw new Error('Unknown presentation');
      revision++;
      cache({ preference: id, pending: Boolean(session) });
      if (!session) explicitGuestChoice = id;
      emit({ ...display(id), loading: false, error: false, pending: false });
      return session ? persist(id) : Promise.resolve();
    },
    retry() { if (state.loadError) return this.connect(session, lastLoad); return session && state.preference && catalog.accepts(state.preference) ? persist(state.preference) : Promise.resolve(); },
    clearGuestTransfer() { explicitGuestChoice = null; },
  };
}
